import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import { FieldArray, useFormikContext } from 'formik';
import chunkArray from 'lodash/chunk';
import type { ReactElement } from 'react';

import type { ProjectFormikType } from '@core/formik/project';
import { CustomFieldDefinition } from '@core/types/api/CustomFieldDefinition/CustomFieldDefinition';

import { Input, type InputProps } from '../../../../components/Input/Input';
import { LoadingSpinner } from '../../../../components/LoadingSpinner/LoadingSpinner';
import { LocalLookupInput } from '../../../../components/LookupInput/LocalLookupInput';
import { useSlabQuery } from '../../../../hooks/useSlabQuery';

export const CustomFieldsSection = (): ReactElement => {
  const formikBag = useFormikContext<ProjectFormikType>();

  const {
    data: fieldDefinitions,
    isLoading: isLoadingFieldDefinitions,
    isError: isErrorFieldDefinitions,
  } = useSlabQuery('GET custom field definitions', {});

  if (isLoadingFieldDefinitions) {
    return <LoadingSpinner />;
  }

  if (isErrorFieldDefinitions || fieldDefinitions === undefined) {
    return <div>ERROR</div>;
  }

  // TODO: #1237 this should no longer be needed.
  const inputType = (wireType: string): Pick<InputProps, 'type'> => {
    // We have two types of CustomFieldDefinitions intended for text input:
    // CFD type 'string' is intended for small amounts of text, while CFD
    // type 'text' is intended for larger notes.
    if (wireType === 'string') {
      return { type: 'text' };
    }
    if (wireType === 'text') {
      return { type: 'textarea' };
    }
    return { type: wireType.toLowerCase() } as unknown as Pick<InputProps, 'type'>;
  };

  return (
    <Box display='flex' flexDirection='column' gap='1rem'>
      <FieldArray
        name='customFields'
        render={(): ReactElement => (
          <>
            {chunkArray(formikBag.values.customFields, 2).map((cfChunk, chunkIdx) => (
              // eslint-disable-next-line react/no-array-index-key
              <Grid key={`custom-field-chunk-${chunkIdx}`} container spacing={2}>
                {cfChunk.map((cf, cfIdx) => {
                  const definition =
                    fieldDefinitions.items.find((cfd) => cfd.id === cf.definition.id) ??
                    CustomFieldDefinition.zero();
                  return (
                    <Grid key={`custom-field-input-${chunkIdx * 2 + cfIdx}`} item xs={6}>
                      {definition.options === null ? (
                        <Input
                          name={`customFields.${chunkIdx * 2 + cfIdx}.value`}
                          label={definition.isRequired ? `${definition.name}*` : definition.name}
                          type={inputType(definition.type).type}
                        />
                      ) : (
                        <LocalLookupInput
                          formState={formikBag.values}
                          name={`customFields.${chunkIdx * 2 + cfIdx}.value`}
                          label={definition.isRequired ? `${definition.name}*` : definition.name}
                          options={definition.options.map((opt) => ({
                            label: opt.label,
                            value: String(opt.value),
                          }))}
                        />
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            ))}
          </>
        )}
      />
    </Box>
  );
};
